import {
  Milk,
  Wheat,
  Package,
  Server,
  Plug,
  Microscope,
  Settings,
  Wrench,
  UtensilsCrossed,
} from "lucide-react";
import type {
  DomainId,
  DomainSidebarConfig,
  DomainSidebarItem,
  EntityPattern,
  RailItem,
} from "./types";

/**
 * Global rail items - icon-only navigation always visible in browse mode
 *
 * Structure:
 * - Top section: Domains (Fermentation, Harvest, Analytics, Inventory, Brewcontrol)
 * - Bottom section: Settings
 */
export const globalRailItems: RailItem[] = [
  {
    type: "link",
    id: "fermentation",
    icon: Milk,
    href: "/fermentations",
    label: "Fermentation",
  },
  {
    type: "link",
    id: "harvest",
    icon: Wheat,
    href: "/harvests",
    label: "Harvest",
  },
  // {
  //   type: "link",
  //   id: "batches",
  //   icon: Layers,
  //   href: "/batches",
  //   label: "Batches",
  // },
  {
    type: "link",
    id: "analytics",
    icon: Microscope,
    href: "/analytics",
    label: "Analytics",
  },
  {
    type: "link",
    id: "food-app",
    icon: UtensilsCrossed,
    href: "/food-app",
    label: "Food Application",
  },
  {
    type: "link",
    id: "inventory",
    icon: Package,
    href: "/inventory",
    label: "Inventory",
  },
  {
    type: "link",
    id: "brewcontrol",
    icon: Server,
    href: "/brewcontrol",
    label: "Brewcontrol",
  },
  {
    type: "link",
    id: "devices",
    icon: Plug,
    href: "/devices",
    label: "Devices",
  },
  // The pre-redesign analysis pages, kept while operators switch over.
  {
    type: "link",
    id: "analytics-classic",
    icon: Wrench,
    href: "/analytics/classic",
    label: "Analytics (classic)",
  },
  { type: "divider" },
  // Settings
  {
    type: "link",
    id: "settings",
    icon: Settings,
    href: "/settings",
    label: "Settings",
    capability: "settings",
  },
];

/**
 * Domain sidebar configurations - shown when a domain is active in browse mode
 */
export const domainSidebars: Partial<Record<DomainId, DomainSidebarConfig>> = {
  fermentation: {
    label: "Fermentation",
    items: [
      // Content pages
      {
        type: "link",
        id: "runs",
        label: "Runs",
        href: "/fermentations",
        category: "content",
      },
      {
        type: "link",
        id: "bottles",
        label: "Bottles",
        href: "/fermentations/bottles",
        category: "content",
      },
      {
        type: "link",
        id: "canvas",
        label: "Canvas",
        href: "/fermentations/canvas",
        category: "content",
      },
      {
        type: "link",
        id: "investigations",
        label: "Investigations",
        href: "/fermentations/investigations",
        category: "content",
      },
      {
        type: "link",
        id: "methanol-suggestions",
        label: "Methanol suggestions",
        href: "/fermentations/methanol-suggestions",
        category: "content",
      },
      { type: "divider" },
      // Schema pages
      {
        type: "link",
        id: "protocols",
        label: "Protocols",
        href: "/fermentations/protocols",
        category: "schema",
      },
      {
        type: "link",
        id: "fermentation-dashboards",
        label: "Dashboards",
        href: "/fermentations/dashboards",
        category: "schema",
      },
      {
        type: "link",
        id: "procedures",
        label: "Procedures",
        href: "/fermentations/procedures",
        category: "schema",
      },
      {
        type: "link",
        id: "medium-recipes",
        label: "Medium Recipes",
        href: "/fermentations/medium-recipes",
        category: "schema",
      },
    ],
  },

  harvest: {
    label: "Harvest",
    items: [
      // Content pages
      {
        type: "link",
        id: "runs",
        label: "Runs",
        href: "/harvests",
        category: "content",
      },
      {
        type: "link",
        id: "batches",
        label: "Batches",
        href: "/harvests/batches",
        category: "content",
      },
      { type: "divider" },
      // Schema pages
      {
        type: "link",
        id: "protocols",
        label: "Protocols",
        href: "/harvests/protocols",
        category: "schema",
      },
      {
        type: "link",
        id: "operation-templates",
        label: "Templates",
        href: "/harvests/operation-templates",
        category: "schema",
        capability: "harvest-template:edit",
      },
    ],
  },

  batches: {
    label: "Batches",
    items: [
      {
        type: "link",
        id: "list",
        label: "List",
        href: "/batches",
        category: "content",
      },
      {
        type: "link",
        id: "clearance",
        label: "Clearance",
        href: "/settings/batch-clearance",
        category: "content",
      },
    ],
  },

  analytics: {
    label: "Analytics",
    items: [
      // Content pages: the overview, one queue per in-house analysis, then
      // the external labs.
      {
        type: "link",
        id: "overview",
        label: "Overview",
        href: "/analytics",
        category: "content",
      },
      {
        type: "link",
        id: "biogenic-amines",
        label: "Biogenic amines",
        href: "/analytics/biogenic-amines",
        category: "content",
      },
      {
        type: "link",
        id: "extracellular-amines",
        label: "Extracellular amines",
        href: "/analytics/extracellular-amines",
        category: "content",
      },
      {
        type: "link",
        id: "lps-endotoxin",
        label: "In-house LAL",
        href: "/analytics/lps-endotoxin",
        category: "content",
      },
      // Every other method's queue, generated from the methods there are.
      { type: "methods" },
      {
        type: "link",
        id: "external",
        label: "External labs",
        href: "/analytics/external",
        category: "content",
      },
      { type: "divider" },
      // Schema pages
      {
        type: "link",
        id: "templates",
        label: "Templates",
        href: "/analytics/templates",
        category: "schema",
        capability: "test-template:edit",
      },
      {
        type: "link",
        id: "blueprints",
        label: "Blueprints",
        href: "/analytics/blueprints",
        category: "schema",
        capability: "test-template:edit",
      },
      {
        type: "link",
        id: "results-catalogue",
        label: "Catalogue",
        href: "/analytics/catalogue",
        category: "schema",
        capability: "test-attribute-code:edit",
      },
      {
        type: "link",
        id: "external-labs",
        label: "Lab partners",
        href: "/analytics/labs",
        category: "schema",
        capability: "test-template:edit",
      },
    ],
  },

  inventory: {
    label: "Inventory",
    items: [
      // Content pages
      {
        type: "link",
        id: "items",
        label: "Items",
        href: "/inventory",
        category: "content",
      },
      {
        type: "link",
        id: "orders",
        label: "Orders",
        href: "/inventory/orders",
        category: "content",
      },
      {
        type: "link",
        id: "receiving",
        label: "Receiving",
        href: "/inventory/receiving",
        category: "content",
      },
      {
        type: "link",
        id: "equipment",
        label: "Equipment",
        href: "/inventory/equipment",
        category: "content",
      },
      {
        type: "link",
        id: "organisms",
        label: "Organisms",
        href: "/inventory/organisms",
        category: "content",
      },
      {
        type: "link",
        id: "analytics",
        label: "Analytics",
        href: "/inventory/analytics",
        category: "content",
      },
      {
        type: "link",
        id: "prepare",
        label: "Prepare",
        href: "/inventory/prepare/recipe",
        category: "content",
      },
      { type: "divider" },
      // Schema pages
      {
        type: "link",
        id: "recipes",
        label: "Recipes",
        href: "/inventory/recipes",
        category: "schema",
      },
      {
        type: "link",
        id: "products",
        label: "Products",
        href: "/inventory/products",
        category: "schema",
      },
      {
        type: "link",
        id: "suppliers",
        label: "Suppliers",
        href: "/inventory/suppliers",
        category: "schema",
      },
    ],
  },

  "food-app": {
    label: "Food App",
    items: [
      // Content pages
      {
        type: "link",
        id: "sessions",
        label: "Sessions",
        href: "/food-app",
        category: "content",
      },
      {
        type: "link",
        id: "projects",
        label: "Projects",
        href: "/food-app/projects",
        category: "content",
      },
      {
        type: "link",
        id: "tastings",
        label: "Tastings",
        href: "/food-app/tastings",
        category: "content",
      },
      {
        type: "link",
        id: "tasters",
        label: "Tasters",
        href: "/food-app/tasters",
        category: "content",
      },
      {
        type: "link",
        id: "sensory",
        label: "Brew Sensory",
        href: "/food-app/sensory",
        category: "content",
      },
      { type: "divider" },
      // Schema pages
      {
        type: "link",
        id: "ingredients",
        label: "Ingredients",
        href: "/food-app/ingredients",
        category: "schema",
      },
      {
        type: "link",
        id: "configuration",
        label: "Configuration",
        href: "/food-app/configuration",
        category: "schema",
      },
    ],
  },

  // brewcontrol uses useSidebarConfig for dynamic node list — no static domain sidebar

  settings: {
    label: "Settings",
    items: [
      {
        type: "section",
        id: "fermentation-settings",
        label: "Fermentation",
        defaultExpanded: true,
        children: [
          {
            type: "link",
            id: "fermentation-params",
            label: "Parameters & Feed Lines",
            href: "/settings/fermentation-parameters",
          },
          {
            type: "link",
            id: "formula-templates",
            label: "Formula Templates",
            href: "/settings/formula-templates",
            capability: "formula-template:edit",
          },
        ],
      },
      {
        type: "section",
        id: "brewcontrol-settings",
        label: "Brewcontrol",
        defaultExpanded: true,
        capability: "brewcontrol-nodes:manage",
        children: [
          {
            type: "link",
            id: "devices",
            label: "Devices",
            href: "/settings/devices",
          },
        ],
      },
      {
        type: "section",
        id: "harvest-settings",
        label: "Harvest",
        defaultExpanded: true,
        capability: "batch-clearance:configure",
        children: [
          {
            type: "link",
            id: "batch-clearance",
            label: "Batch Clearance",
            href: "/settings/batch-clearance",
          },
        ],
      },
      {
        type: "section",
        id: "inventory-settings",
        label: "Inventory",
        defaultExpanded: true,
        capability: "inventory:manage",
        children: [
          {
            type: "link",
            id: "resources",
            label: "Resources",
            href: "/settings/inventory/resources",
          },
          {
            type: "link",
            id: "locations",
            label: "Locations",
            href: "/settings/inventory/locations",
          },
          {
            type: "link",
            id: "manufacturers",
            label: "Manufacturers",
            href: "/settings/inventory/manufacturers",
          },
          {
            type: "link",
            id: "certificate-types",
            label: "Certificate Types",
            href: "/settings/inventory/certificate-types",
          },
          {
            type: "link",
            id: "equipment-types",
            label: "Equipment Types",
            href: "/settings/inventory/equipment-types",
          },
          {
            type: "link",
            id: "equipment-models",
            label: "Equipment Models",
            href: "/settings/inventory/equipment-models",
          },
          {
            type: "link",
            id: "sterilization",
            label: "Sterilization",
            href: "/settings/inventory/sterilization",
            capability: "inventory:manage",
          },
        ],
      },
      {
        type: "link",
        id: "cleaning",
        label: "Cleaning Procedures",
        href: "/settings/cleaning-procedures",
        capability: "cleaning-procedures:edit",
      },
      {
        type: "link",
        id: "operators",
        label: "Access",
        href: "/settings/operators",
        capability: "operators:manage-capabilities",
      },
      {
        type: "link",
        id: "documents",
        label: "Documents",
        href: "/settings/documents",
      },
    ],
  },
};

/**
 * Entity route patterns - used to detect when we're in entity mode
 *
 * Deep entities: complex, many sections, focused work
 * Shallow entities: few items, quick switching needed
 */
export const entityPatterns: EntityPattern[] = [
  // Fermentation
  {
    pattern: "/fermentations/$group_id",
    type: "deep",
    parent: { label: "Fermentation Runs", href: "/fermentations" },
  },
  {
    pattern: "/fermentations/protocols/$protocol_id",
    type: "shallow",
    parent: { label: "Protocols", href: "/fermentations/protocols" },
  },
  {
    pattern: "/fermentations/investigations/$investigation_id",
    type: "deep",
    parent: { label: "Investigations", href: "/fermentations/investigations" },
  },
  {
    pattern: "/fermentations/procedures/$procedure_id",
    type: "shallow",
    parent: { label: "Procedures", href: "/fermentations/procedures" },
  },
  {
    pattern: "/fermentations/medium-recipes/$recipe_id",
    type: "shallow",
    parent: { label: "Medium Recipes", href: "/fermentations/medium-recipes" },
  },

  // Harvest
  {
    pattern: "/harvests/$group_id",
    type: "deep",
    parent: { label: "Harvest Runs", href: "/harvests" },
  },
  {
    pattern: "/harvests/protocols/$protocol_id",
    type: "shallow",
    parent: { label: "Protocols", href: "/harvests/protocols" },
  },
  {
    pattern: "/harvests/operation-templates/$template_id",
    type: "shallow",
    parent: { label: "Templates", href: "/harvests/operation-templates" },
  },
  {
    pattern: "/harvests/batches/$batch_id",
    type: "shallow",
    parent: { label: "Batches", href: "/harvests/batches" },
  },

  // Batches
  {
    pattern: "/batches/$batch_id",
    type: "deep",
    parent: { label: "Batches", href: "/batches" },
  },

  // Analytics
  {
    pattern: "/analytics/templates/$template_id",
    type: "shallow",
    parent: { label: "Test Templates", href: "/analytics/templates" },
  },

  // Inventory
  {
    pattern: "/inventory/equipment/$equipment_id",
    type: "shallow",
    parent: { label: "Equipment", href: "/inventory/equipment" },
  },
  {
    pattern: "/inventory/recipes/$recipeId",
    type: "shallow",
    parent: { label: "Recipes", href: "/inventory/recipes" },
  },

  // Settings
  {
    pattern: "/settings/formula-templates",
    type: "shallow",
    parent: { label: "Settings", href: "/settings" },
  },
  {
    pattern: "/settings/batch-clearance/$clearance_defintion_id",
    type: "shallow",
    parent: { label: "Batch Clearance", href: "/settings/batch-clearance" },
  },
  {
    pattern: "/settings/cleaning-procedures/$procedure_id",
    type: "shallow",
    parent: {
      label: "Cleaning Procedures",
      href: "/settings/cleaning-procedures",
    },
  },
];

/**
 * Path prefixes where the domain sidebar panel should be suppressed.
 * The global rail remains visible; only the secondary panel is hidden.
 */
export const noDomainSidebarPaths: string[] = ["/food-app/sessions/"];

/**
 * Get the domain ID from a pathname
 */
export function getDomainFromPath(pathname: string): DomainId | null {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/fermentations")) return "fermentation";
  if (pathname.startsWith("/harvests")) return "harvest";
  if (pathname.startsWith("/batches")) return "batches";
  if (pathname.startsWith("/inventory")) return "inventory";
  if (pathname.startsWith("/brewcontrol")) return "brewcontrol";
  if (pathname.startsWith("/food-app")) return "food-app";
  if (pathname.startsWith("/devices")) return "devices";
  if (pathname.startsWith("/analytics/classic")) return "analytics-classic";
  if (pathname.startsWith("/analytics")) return "analytics";
  if (pathname.startsWith("/settings")) return "settings";
  return null;
}

/**
 * Check if a pathname matches an entity pattern
 */
export function matchEntityPattern(pathname: string): EntityPattern | null {
  for (const pattern of entityPatterns) {
    if (matchesPattern(pathname, pattern.pattern)) {
      return pattern;
    }
  }
  return null;
}

/**
 * Match a pathname against a pattern with $ params
 * e.g. "/fermentations/123" matches "/fermentations/$group_id"
 *
 * Entity IDs must be numeric to avoid matching literal route segments
 * (e.g., "/fermentations/protocols" should NOT match "/fermentations/$group_id")
 */
function matchesPattern(pathname: string, pattern: string): boolean {
  const patternParts = pattern.split("/");
  const pathParts = pathname.split("/");

  // Pattern must be a prefix of the path (allow nested routes)
  if (pathParts.length < patternParts.length) {
    return false;
  }

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];

    // $ params match numeric IDs only (to avoid matching literal route segments)
    if (patternPart.startsWith("$")) {
      if (!pathPart || pathPart === "" || !isNumericId(pathPart)) {
        return false;
      }
      continue;
    }

    // Literal segments must match exactly
    if (patternPart !== pathPart) {
      return false;
    }
  }

  return true;
}

/**
 * Check if a string looks like a numeric ID
 * Matches integers like "123" but not route segments like "protocols"
 */
function isNumericId(segment: string): boolean {
  return /^\d+$/.test(segment);
}
